import {
  ssfeaturesCardSx,
  ssfeaturesFocusSx,
  ssfeaturesPanelSx,
  ssfeaturesTabButtonBaseSx,
  ssfeaturesTabButtonSelectedSx,
  ssfeaturesTabButtonSx,
  ssfeaturesTokens,
} from '../products/SSFeatures/ssfeaturesStyles';

export const publicMarketingTokens = ssfeaturesTokens;
export {
  ssfeaturesCardSx as publicCardSx,
  ssfeaturesFocusSx as publicFocusSx,
  ssfeaturesPanelSx as publicPanelSx,
  ssfeaturesTabButtonBaseSx as publicTabButtonBaseSx,
  ssfeaturesTabButtonSelectedSx as publicTabButtonSelectedSx,
  ssfeaturesTabButtonSx as publicTabButtonSx,
};

/** The single source of truth for the Smarthub surface gradient (public + authenticated). */
export const publicBackdropGradientSx = {
  backgroundColor: '#05070a',
  // Final linear-gradient layer is fully opaque, so this stack occludes any global App backdrop beneath it.
  backgroundImage:
    'radial-gradient(circle at 18% 20%, rgba(0, 180, 255, 0.10), transparent 32%), radial-gradient(circle at 82% 48%, rgba(30, 64, 175, 0.14), transparent 30%), linear-gradient(180deg, #05070a 0%, #070b14 52%, #05070a 100%)',
};

export const publicPageRootSx = {
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflowX: 'hidden',
  color: ssfeaturesTokens.color.text,
  ...publicBackdropGradientSx,
};

/** Public header / mega / mobile panel — glass over 2026 liquid-light backdrop */
export const publicHeaderTokens = {
  barBg: 'rgba(5, 7, 10, 0.72)',
  barBgSolid: '#05070a',
  panelBg: 'rgba(5, 7, 10, 0.92)',
  border: 'rgba(96, 165, 250, 0.22)',
  borderSoft: 'rgba(0, 229, 255, 0.14)',
  blur: 'blur(14px)',
  accent: '#00e5ff',
  accentSoft: 'rgba(0, 229, 255, 0.14)',
};

export const publicPageWidths = {
  narrow: 800,
  medium: 960,
  wide: 1280,
  /**
   * Shared content + chrome width for public header / mega / footer /
   * Home / Pricing / About / Catalogue.
   * Same max width + horizontal padding keeps nav links and page content aligned.
   */
  chrome: 1800,
  home: 1800,
  pricing: 1800,
  about: 1800,
  catalogue: 1800,
  learn: 1800,
  creators: 1800,
  partners: 1800,
  security: 1800,
  faq: 1800,
  legal: 1800,
  contact: 1800,
  auth: 1800,
};

/** Opaque navy auth form panel — matches Home/Security cards (no glass). */
export const publicAuthPanelSx = {
  ...ssfeaturesPanelSx,
  backgroundColor: '#0a1628',
  backgroundImage: 'none',
  boxShadow: 'none',
  border: '1px solid rgba(96, 165, 250, 0.18)',
  borderRadius: ssfeaturesTokens.radius?.panel || '18px',
  width: '100%',
  boxSizing: 'border-box',
};

/** Shared outlined field look for login / signup / reset forms. */
export const publicAuthFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: { xs: 52, sm: 50, md: 48 },
    bgcolor: 'rgba(8, 19, 35, 0.9)',
    borderRadius: '10px',
    transition: `border-color ${ssfeaturesTokens.motion.fast}, background-color ${ssfeaturesTokens.motion.fast}`,
    '& fieldset': { borderColor: 'rgba(96, 165, 250, 0.22)' },
    '&:hover': {
      bgcolor: 'rgba(10, 24, 42, 0.96)',
      '& fieldset': { borderColor: 'rgba(147, 197, 253, 0.4)' },
    },
    '&.Mui-focused': {
      bgcolor: 'rgba(10, 24, 42, 1)',
      '& fieldset': { borderColor: '#60a5fa', borderWidth: '1.5px' },
    },
    '& input': {
      fontFamily: ssfeaturesTokens.fontFamily,
      fontSize: { xs: '1rem', sm: '0.95rem' },
      lineHeight: 1.4,
      py: { xs: 1.5, sm: 1.35 },
      px: 0.5,
      color: '#f8fafc',
      // Prevent iOS zoom-on-focus (needs >= 16px)
      '&::placeholder': { color: 'rgba(148, 163, 184, 0.85)', opacity: 1 },
    },
    '& .MuiInputAdornment-root, & .MuiIconButton-root': {
      color: 'rgba(148, 163, 184, 0.9)',
    },
  },
};

/** Persistent labels above auth fields (sentence case, not all-caps). */
export const publicAuthLabelSx = {
  display: 'block',
  width: '100%',
  m: 0,
  mb: { xs: 1, sm: 0.85 },
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '0.9rem', sm: '0.875rem' },
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'rgba(226, 232, 240, 0.94)',
  letterSpacing: '-0.01em',
  textAlign: 'left',
};

/** Vertical rhythm between auth field groups (label+input). */
export const publicAuthStackGap = {
  xs: 2.75,
  sm: 2.85,
  md: 3,
};

/** Comfortable auth column width inside the 1800 chrome. */
export const publicAuthColumnMaxWidth = {
  xs: '100%',
  sm: 520,
  md: 560,
  lg: 600,
  xl: 640,
};

/** Full-viewport auth loading / success transitions — opaque new navy, no old bg bleed. */
export const publicAuthTransitionOverlaySx = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
  minHeight: '100dvh',
  ...publicBackdropGradientSx,
};

/** Left-aligned account switch row under the primary CTA. */
export const publicAuthSwitchRowSx = {
  width: '100%',
  mt: { xs: 0.5, sm: 0.75 },
  pt: { xs: 2.25, sm: 2.5 },
  borderTop: '1px solid rgba(96, 165, 250, 0.14)',
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'flex-start',
  gap: { xs: 1, sm: 1.25 },
  textAlign: 'left',
};

export const publicAuthSwitchTextSx = {
  m: 0,
  color: 'rgba(148, 163, 184, 0.95)',
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '0.9rem', sm: '0.875rem' },
  lineHeight: 1.4,
  fontWeight: 500,
};

export const publicAuthSwitchActionSx = {
  color: '#93c5fd',
  textDecoration: 'none',
  fontWeight: 650,
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '0.95rem', sm: '0.875rem' },
  lineHeight: 1.3,
  background: 'none',
  border: 'none',
  p: 0,
  minHeight: { xs: 44, sm: 'auto' },
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  '&:hover': { textDecoration: 'underline', color: '#bfdbfe' },
};

/** Shared horizontal padding for public header, mega menu, and footer */
export const publicChromePadSx = {
  px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
};

/** Authenticated hub pages — same max width + gutters as the header / dashboard. */
export const hubChromeContentSx = {
  width: '100%',
  maxWidth: publicPageWidths.chrome,
  mx: 'auto',
  ...publicChromePadSx,
  boxSizing: 'border-box',
};

export function getPublicPageShellSx(variant = 'wide') {
  return {
    position: 'relative',
    zIndex: 2,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: publicPageWidths[variant] || publicPageWidths.wide,
    mx: 'auto',
    ...publicChromePadSx,
    py: { xs: 3.5, sm: 5, md: 6.5, lg: 8 },
    boxSizing: 'border-box',
  };
}

export const publicSectionSpacingSx = { xs: 5.5, sm: 6.5, md: 8, lg: 9 };

export const publicHeroKickerSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  pl: 1.25,
  py: 0.1,
  borderLeft: '2px solid rgba(96, 165, 250, 0.72)',
  color: ssfeaturesTokens.color.textSubtle,
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '0.75rem', sm: '0.8rem' },
  fontWeight: 600,
  letterSpacing: '0.06em',
  lineHeight: 1.3,
  textTransform: 'uppercase',
};

export const publicHeroTitleSx = {
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: {
    xs: 'clamp(2rem, 9vw, 2.65rem)',
    sm: 'clamp(2.6rem, 7vw, 3.4rem)',
    md: 'clamp(3rem, 5vw, 4rem)',
  },
  fontWeight: 700,
  color: ssfeaturesTokens.color.text,
  lineHeight: 1.08,
  letterSpacing: '-0.035em',
};

export const publicHeroBodySx = {
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '1rem', sm: '1.08rem', md: '1.15rem' },
  fontWeight: 400,
  color: ssfeaturesTokens.color.textMuted,
  lineHeight: 1.7,
};

export const publicSectionEyebrowSx = {
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: '0.72rem',
  fontWeight: 700,
  color: ssfeaturesTokens.color.accent,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
};

export const publicSectionTitleSx = {
  fontFamily: ssfeaturesTokens.fontFamily,
  fontSize: { xs: '1.45rem', sm: '1.7rem', md: '2rem' },
  fontWeight: 650,
  color: ssfeaturesTokens.color.text,
  lineHeight: 1.2,
  letterSpacing: '-0.025em',
};
